import {
    SectionCard,
    SectionCardH1,
    SectionCardHeading,
    SectionCardSubH1,
} from '../SectionCard';
import { tooltips, views } from '@/copy';
import { DATA_SOURCES } from '@/config/constants';
import DefinitionTooltip from '../DefinitionTooltip';
import TopPhrasesSection from './TopPhrasesSection';
import { downloadItemsAsCSV } from 'utils/formatCSV';
import { getTikTokData } from 'utils/api/tiktok.api';
import { useCallback } from 'react';
import { useQuery } from 'react-query';

const hashtagCSVColumns = [
    { key: 'kw', label: 'hashtag' },
    { key: 'volume', label: 'views' },
];

function TopHashtagsSectionHeading({ name, data, children, ...props }) {
    return (
        <SectionCardHeading {...props}>
            <SectionCardH1>Top Hashtags</SectionCardH1>
            <SectionCardSubH1>
                for <span className="capitalize">{name}</span> by Views
                <span css="position: relative; top: 2px;">
                    <DefinitionTooltip
                        {...tooltips.non_cum_avg_weekly_views}
                        week={data?.week}
                        year={data?.year}
                        mixpanelMeta={{ contentKey: 'hashtagsSection' }}
                        openOnClick={true}
                    />
                </span>{' '}
                <span className="capitalize">{views.descending}</span>
            </SectionCardSubH1>
            {children}
        </SectionCardHeading>
    );
}

function NoHashtags() {
    return (
        <div
            css={`
                color: ${({ theme }) => theme.neutral_3};
            `}
        >
            No hashtags to show.
        </div>
    );
}

function TopHashtagsSection({
    enabled,
    data,
    paginateBy,
    controller,
    filename,
    WrapperComponent = SectionCard,
    HeadingComponent = TopHashtagsSectionHeading,
    NoDataComponent = NoHashtags,
    ...props
}) {
    const queryFn = useCallback(({ queryKey }) =>
        getTikTokData({
            data: { ...queryKey[1] },
        }),
    );
    const { week, year, brands, categories, countries } = data;
    const hashtagsDataQuery = useQuery({
        queryKey: [
            'TikTokData',
            {
                brand: brands,
                category: categories,
                country: countries,
                hashtag: '*',
                page: 1,
                per_page: 1000,
                order_by: 'non_cum_avg_weekly_views',
                ascending: false,
                week: [week],
                year: [year],
            },
        ],
        queryFn,
        enabled,
    });

    const sortedData =
        hashtagsDataQuery.data?.data?.map(
            ({ id, hashtag, hashtag_name, non_cum_avg_weekly_views }) => ({
                kw: hashtag_name,
                fetching: hashtagsDataQuery.isLoading,
                fetchingData: hashtagsDataQuery.isLoading,
                id: hashtag,
                volume: non_cum_avg_weekly_views,
                flagId: id,
            }),
        ) || null;

    const handleDownload = () =>
        downloadItemsAsCSV(filename, sortedData, hashtagCSVColumns);

    const isLoading = [hashtagsDataQuery.status].includes('loading');
    const dataReady = sortedData && hashtagsDataQuery.isSuccess;

    const mixpanelMeta = { contentKey: 'TopHashtags' };

    return (
        <TopPhrasesSection
            data={{ ...data, dataSource: DATA_SOURCES.TIKTOK }}
            isLoading={isLoading}
            sortedData={sortedData}
            dataReady={dataReady}
            showTranslations={false}
            handleDownload={handleDownload}
            mixpanelMeta={mixpanelMeta}
            paginateBy="hashtags"
            WrapperComponent={WrapperComponent}
            HeadingComponent={HeadingComponent}
            NoDataComponent={NoDataComponent}
            {...props}
        />
    );
}

export default TopHashtagsSection;
